import type { ComponentProps } from 'react'
import type { Insight } from '@/features/insights/utils/types'

type SvgProps = ComponentProps<'svg'>

interface InsightThumbnailProps {
  insight: Insight
}

/**
 * Loading-state placeholder for insight cards. The real card visuals below are
 * pre-generated SVG families selected by insight kind and a stable metadata key.
 */
export function InsightThumbnailPlaceholder() {
  return (
    <div className="flex h-24 w-full items-center justify-center rounded-md bg-muted/60 text-muted-foreground/70">
      <BaseSvg>
        <line x1="22" y1="20" x2="50" y2="32" />
        <line x1="50" y1="32" x2="78" y2="22" />
        <line x1="50" y1="32" x2="64" y2="46" />
        <line x1="78" y1="22" x2="98" y2="32" />
        <line x1="64" y1="46" x2="92" y2="46" />
        <Node cx="22" cy="20" r="4" opacity={0.35} />
        <Node cx="50" cy="32" r="5" opacity={0.45} />
        <Node cx="78" cy="22" r="4" opacity={0.35} />
        <Node cx="64" cy="46" r="3.5" opacity={0.3} />
        <Node cx="98" cy="32" r="3.5" opacity={0.3} />
        <Node cx="92" cy="46" r="3.5" opacity={0.3} />
      </BaseSvg>
    </div>
  )
}

export function InsightThumbnail({ insight }: InsightThumbnailProps) {
  const variant = getVariantIndex(insight)

  return (
    <div className="flex h-24 w-full items-center justify-center rounded-md bg-muted/60 text-muted-foreground/80">
      {insight.kind === 'cluster' ? (
        <ClusterThumbnail variant={variant} />
      ) : insight.kind === 'top_entity' ? (
        <TopEntityThumbnail variant={variant} />
      ) : (
        <BridgeThumbnail variant={variant} />
      )}
    </div>
  )
}

function getVariantIndex(insight: Insight) {
  const seed =
    insight.metadata.kind === 'cluster'
      ? `cluster:${insight.metadata.componentId}:${insight.metadata.entityCount}:${insight.metadata.relationshipCount ?? 0}`
      : insight.metadata.kind === 'top_entity'
        ? `entity:${insight.metadata.entityId}:${insight.metadata.degree ?? 0}:${insight.metadata.sourceCount ?? 0}`
        : `bridge:${insight.metadata.componentA}:${insight.metadata.componentB}`

  return hashString(seed) % 3
}

/** Stable variant in [0,2] for any string seed — used outside the insight context
 *  (e.g. the identity detail page picks a thumbnail by `identity.id`). */
export function variantForSeed(seed: string): number {
  return hashString(seed) % 3
}

function hashString(value: string) {
  let hash = 0
  for (let i = 0; i < value.length; i += 1) {
    hash = (hash * 31 + value.charCodeAt(i)) >>> 0
  }
  return hash
}

function BaseSvg({ children, className, ...props }: SvgProps) {
  return (
    <svg
      viewBox="0 0 120 60"
      className={className ?? 'h-full w-auto'}
      aria-hidden="true"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <g strokeOpacity="0.4">{children}</g>
    </svg>
  )
}

function Node({ opacity, ...props }: ComponentProps<'circle'> & { opacity: number }) {
  return <circle {...props} fill="currentColor" fillOpacity={opacity} stroke="none" />
}

function AccentNode({ opacity, ...props }: ComponentProps<'circle'> & { opacity: number }) {
  return (
    <circle
      {...props}
      fill="currentColor"
      fillOpacity={opacity}
      className="text-primary"
      stroke="none"
    />
  )
}

function ClusterThumbnail({ variant }: { variant: number }) {
  if (variant === 1) {
    return (
      <BaseSvg>
        <line x1="22" y1="20" x2="44" y2="30" />
        <line x1="44" y1="30" x2="60" y2="18" />
        <line x1="44" y1="30" x2="68" y2="42" />
        <line x1="60" y1="18" x2="82" y2="24" />
        <line x1="68" y1="42" x2="92" y2="40" />
        <line x1="60" y1="18" x2="68" y2="42" />
        <Node cx="22" cy="20" r="3.5" opacity={0.28} />
        <Node cx="44" cy="30" r="5.5" opacity={0.48} />
        <AccentNode cx="60" cy="18" r="4.5" opacity={0.65} />
        <Node cx="68" cy="42" r="4" opacity={0.34} />
        <Node cx="82" cy="24" r="3.5" opacity={0.28} />
        <Node cx="92" cy="40" r="3.5" opacity={0.28} />
      </BaseSvg>
    )
  }

  if (variant === 2) {
    return (
      <BaseSvg>
        <line x1="26" y1="18" x2="46" y2="24" />
        <line x1="46" y1="24" x2="70" y2="18" />
        <line x1="46" y1="24" x2="56" y2="42" />
        <line x1="56" y1="42" x2="82" y2="40" />
        <line x1="70" y1="18" x2="94" y2="28" />
        <line x1="46" y1="24" x2="82" y2="40" />
        <line x1="56" y1="42" x2="70" y2="18" />
        <Node cx="26" cy="18" r="3.5" opacity={0.28} />
        <AccentNode cx="46" cy="24" r="5.5" opacity={0.65} />
        <Node cx="70" cy="18" r="4.5" opacity={0.36} />
        <Node cx="56" cy="42" r="4" opacity={0.32} />
        <Node cx="82" cy="40" r="4" opacity={0.32} />
        <Node cx="94" cy="28" r="3.5" opacity={0.28} />
      </BaseSvg>
    )
  }

  return (
    <BaseSvg>
      <line x1="20" y1="24" x2="38" y2="18" />
      <line x1="38" y1="18" x2="54" y2="28" />
      <line x1="54" y1="28" x2="74" y2="18" />
      <line x1="54" y1="28" x2="68" y2="42" />
      <line x1="74" y1="18" x2="96" y2="28" />
      <line x1="68" y1="42" x2="92" y2="42" />
      <Node cx="20" cy="24" r="3.5" opacity={0.28} />
      <Node cx="38" cy="18" r="4" opacity={0.32} />
      <AccentNode cx="54" cy="28" r="5.5" opacity={0.65} />
      <Node cx="74" cy="18" r="4.5" opacity={0.36} />
      <Node cx="68" cy="42" r="4" opacity={0.32} />
      <Node cx="96" cy="28" r="3.5" opacity={0.28} />
      <Node cx="92" cy="42" r="3.5" opacity={0.28} />
    </BaseSvg>
  )
}

export function TopEntityThumbnail({ variant }: { variant: number }) {
  if (variant === 1) {
    return (
      <BaseSvg>
        <line x1="60" y1="30" x2="24" y2="18" />
        <line x1="60" y1="30" x2="36" y2="44" />
        <line x1="60" y1="30" x2="58" y2="14" />
        <line x1="60" y1="30" x2="84" y2="18" />
        <line x1="60" y1="30" x2="96" y2="40" />
        <line x1="84" y1="18" x2="100" y2="12" />
        <Node cx="24" cy="18" r="3.5" opacity={0.28} />
        <Node cx="36" cy="44" r="3.5" opacity={0.28} />
        <Node cx="58" cy="14" r="3.5" opacity={0.28} />
        <AccentNode cx="60" cy="30" r="6" opacity={0.68} />
        <Node cx="84" cy="18" r="4" opacity={0.34} />
        <Node cx="96" cy="40" r="3.5" opacity={0.28} />
        <Node cx="100" cy="12" r="3" opacity={0.24} />
      </BaseSvg>
    )
  }

  if (variant === 2) {
    return (
      <BaseSvg>
        <line x1="58" y1="30" x2="22" y2="26" />
        <line x1="58" y1="30" x2="34" y2="16" />
        <line x1="58" y1="30" x2="36" y2="44" />
        <line x1="58" y1="30" x2="80" y2="16" />
        <line x1="58" y1="30" x2="92" y2="24" />
        <line x1="58" y1="30" x2="92" y2="42" />
        <Node cx="22" cy="26" r="3.5" opacity={0.28} />
        <Node cx="34" cy="16" r="3.5" opacity={0.28} />
        <Node cx="36" cy="44" r="3.5" opacity={0.28} />
        <AccentNode cx="58" cy="30" r="6.5" opacity={0.7} />
        <Node cx="80" cy="16" r="3.5" opacity={0.28} />
        <Node cx="92" cy="24" r="3.5" opacity={0.28} />
        <Node cx="92" cy="42" r="3.5" opacity={0.28} />
      </BaseSvg>
    )
  }

  return (
    <BaseSvg>
      <line x1="60" y1="30" x2="24" y2="18" />
      <line x1="60" y1="30" x2="28" y2="40" />
      <line x1="60" y1="30" x2="48" y2="14" />
      <line x1="60" y1="30" x2="74" y2="16" />
      <line x1="60" y1="30" x2="90" y2="20" />
      <line x1="60" y1="30" x2="96" y2="40" />
      <Node cx="24" cy="18" r="3.5" opacity={0.28} />
      <Node cx="28" cy="40" r="3.5" opacity={0.28} />
      <Node cx="48" cy="14" r="3.5" opacity={0.28} />
      <AccentNode cx="60" cy="30" r="6.5" opacity={0.7} />
      <Node cx="74" cy="16" r="3.5" opacity={0.28} />
      <Node cx="90" cy="20" r="3.5" opacity={0.28} />
      <Node cx="96" cy="40" r="3.5" opacity={0.28} />
    </BaseSvg>
  )
}

function BridgeThumbnail({ variant }: { variant: number }) {
  if (variant === 1) {
    return (
      <BaseSvg>
        <line x1="18" y1="22" x2="34" y2="30" />
        <line x1="34" y1="30" x2="48" y2="22" />
        <line x1="72" y1="22" x2="86" y2="16" />
        <line x1="72" y1="22" x2="98" y2="30" />
        <line
          x1="48"
          y1="22"
          x2="72"
          y2="22"
          className="text-primary"
          stroke="currentColor"
          strokeOpacity="0.75"
        />
        <Node cx="18" cy="22" r="3.5" opacity={0.28} />
        <Node cx="34" cy="30" r="5" opacity={0.4} />
        <AccentNode cx="48" cy="22" r="4" opacity={0.65} />
        <AccentNode cx="72" cy="22" r="4" opacity={0.65} />
        <Node cx="86" cy="16" r="3.5" opacity={0.28} />
        <Node cx="98" cy="30" r="3.5" opacity={0.28} />
      </BaseSvg>
    )
  }

  if (variant === 2) {
    return (
      <BaseSvg>
        <line x1="20" y1="18" x2="36" y2="26" />
        <line x1="36" y1="26" x2="42" y2="40" />
        <line x1="78" y1="20" x2="92" y2="14" />
        <line x1="78" y1="20" x2="94" y2="34" />
        <line
          x1="42"
          y1="40"
          x2="60"
          y2="30"
          className="text-primary"
          stroke="currentColor"
          strokeOpacity="0.75"
        />
        <line
          x1="60"
          y1="30"
          x2="78"
          y2="20"
          className="text-primary"
          stroke="currentColor"
          strokeOpacity="0.75"
        />
        <Node cx="20" cy="18" r="3.5" opacity={0.28} />
        <Node cx="36" cy="26" r="4.5" opacity={0.34} />
        <AccentNode cx="42" cy="40" r="4" opacity={0.65} />
        <AccentNode cx="60" cy="30" r="4.5" opacity={0.72} />
        <AccentNode cx="78" cy="20" r="4" opacity={0.65} />
        <Node cx="92" cy="14" r="3.5" opacity={0.28} />
        <Node cx="94" cy="34" r="3.5" opacity={0.28} />
      </BaseSvg>
    )
  }

  return (
    <BaseSvg>
      <line x1="22" y1="24" x2="38" y2="18" />
      <line x1="38" y1="18" x2="48" y2="28" />
      <line x1="72" y1="28" x2="84" y2="18" />
      <line x1="72" y1="28" x2="98" y2="30" />
      <line
        x1="48"
        y1="28"
        x2="72"
        y2="28"
        className="text-primary"
        stroke="currentColor"
        strokeOpacity="0.75"
      />
      <Node cx="22" cy="24" r="3.5" opacity={0.28} />
      <Node cx="38" cy="18" r="4" opacity={0.32} />
      <AccentNode cx="48" cy="28" r="4.5" opacity={0.65} />
      <AccentNode cx="72" cy="28" r="4.5" opacity={0.65} />
      <Node cx="84" cy="18" r="4" opacity={0.32} />
      <Node cx="98" cy="30" r="3.5" opacity={0.28} />
    </BaseSvg>
  )
}
